import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Link as MaterialLink,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { PaletteColor } from "@mui/material/styles";
import { useUsuario } from "../../../../providers/usuarioProvider";
import Usuario from "../../../../models/usuario";
import { onSnapshot, query, updateDoc, where } from "firebase/firestore";
import UsuarioDb from "../../../../db/usuarioDb";
import BusinessDb from "../../../../db/businessDb";
import Business from "../../../../models/business";
import { filter, includes, isEmpty, isUndefined, map, orderBy } from "lodash";
import {
  IconBriefcase,
  IconBookmark,
  IconLayoutGrid,
  IconLayoutList,
  IconUserPlus,
  IconUser,
} from "@tabler/icons-react";
import PermissaoDisplay from "../../../util/permissaoDisplay";
import CriarClienteDialog from "../../conta/business/criarClienteDialog";
import GradeClientes from "./visualizacao/grade";
import ListaClientes from "./visualizacao/lista";
import { Flags } from "../../../../models/enum";
import FelixTour from "../../../util/felixTour";
import { Link } from "react-router-dom";
import { useAssinar } from "../../assinatura/assine";
import ExtratoBanner, { extratoBannerVisivel } from "./extratoBanner";

/** Cada flag do cliente usa a cor semântica do tema — nunca hex solto.
 *  Selecionada vira preenchimento sólido; a tinta por cima é o contrastText. */
const estiloFlag = (cor: PaletteColor) => ({
  color: cor.main,
  "&.Mui-selected": {
    backgroundColor: cor.main,
    color: cor.contrastText,
    boxShadow: "none",
    "&:hover": { backgroundColor: cor.dark },
  },
});

export default function PainelContador() {
  const { usuario } = useUsuario();
  const abrirAssinatura = useAssinar();
  const [business, setBusiness] = useState<Business>();
  const [clientes, setClientes] = useState<Usuario[]>();
  const [openCriarCliente, setOpenCriarCliente] = useState(false);
  const [pesquisa, setPesquisa] = useState("");
  const [flags, setFlags] = useState<Flags[]>([]);
  const [limite, setLimite] = useState(false);

  useEffect(() => {
    if (!!business)
      return onSnapshot(
        query(
          new UsuarioDb().collection({ usuarioId: "" }),
          where("contaPai", "==", business.id),
        ),
        (snapshot) =>
          setClientes(
            filter(
              orderBy(
                map(snapshot.docs, (doc) => doc.data()),
                "posicao",
              ),
              (cliente) => !!!cliente.arquivado,
            ),
          ),
      );
  }, [business]);

  useEffect(() => {
    if (usuario?.business)
      return onSnapshot(
        new BusinessDb().doc({ businessId: usuario.business }),
        (snapshot) => setBusiness(snapshot.data()),
      );
  }, [usuario?.business]);

  useEffect(() => {
    if ((clientes?.length || 0) > (business?.quantidade || 0)) {
      setLimite(true);
    }
  }, [clientes?.length, business?.quantidade]);

  if (isUndefined(business) || isUndefined(clientes)) return <></>;

  const clientesFiltrados = filter(
    filter(
      clientes,
      (cliente) =>
        cliente.nome.toLowerCase().startsWith(pesquisa.toLowerCase()) ||
        (cliente.cpf || "").toLowerCase().startsWith(pesquisa.toLowerCase()),
    ),
    (cliente) => isEmpty(flags) || includes(flags, cliente.flag),
  );

  const gridItems = [
    {
      key: "header",
      xs: 12,
      md: 12,
      render: () => (
        <Box className="boas-vindas">
          <Typography variant="eyebrow" color="text.secondary">
            Painel do contador
          </Typography>
          <Typography variant="h1" color="text.primary" mt={0.5}>
            {`Olá, `}
            <MaterialLink
              component={Link}
              color="inherit"
              to="/conta"
              underline="hover"
            >
              {usuario?.nome || "Contador"}
            </MaterialLink>
            {` 👋 - `}
            <MaterialLink
              component={Link}
              color="inherit"
              to="/conta#business"
              underline="hover"
              sx={{ fontStyle: "italic", color: "accentInk" }}
            >
              {business?.nome || "Escritório"}
            </MaterialLink>
          </Typography>
        </Box>
      ),
    },
    {
      key: "controles",
      xs: 12,
      render: () => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Box mr={1}>
            <ToggleButtonGroup
              color="primary"
              size="small"
              exclusive
              value={usuario?.layoutPainel || "grade"}
              onChange={(_event, value) => {
                if (!!value && !!usuario)
                  updateDoc(new UsuarioDb().doc({ usuarioId: usuario.id }), {
                    layoutPainel: value,
                  });
              }}
              aria-label="layout"
            >
              <ToggleButton key="grade" value="grade" aria-label="grade">
                <IconLayoutGrid />
              </ToggleButton>

              <ToggleButton key="lista" value="lista" aria-label="lista">
                <IconLayoutList />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box mr={1}>
            <ToggleButtonGroup
              color="standard"
              size="small"
              value={flags}
              onChange={(_event, value) => {
                setFlags(value);
              }}
              sx={(theme) => ({
                "& .MuiToggleButton-success": estiloFlag(theme.palette.success),
                "& .MuiToggleButton-info": estiloFlag(theme.palette.info),
                "& .MuiToggleButton-warning": estiloFlag(theme.palette.warning),
                "& .MuiToggleButton-error": estiloFlag(theme.palette.error),
              })}
              aria-label="flags"
            >
              {map(Object.values(Flags), (value) => (
                <ToggleButton
                  key={value}
                  value={value}
                  aria-label={value}
                  color={value}
                >
                  <IconBookmark />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box mr={1}>
            <TextField
              hiddenLabel
              size="small"
              variant="outlined"
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
              placeholder="Nome ou CPF"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconUser size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box flexGrow={1} />
          <PermissaoDisplay
            match={(claims) => !!claims?.permissoes?.gerenciar_clientes}
          >
            <Box mr={1}>
              <Button
                className="comecar"
                variant="contained"
                color="primary"
                startIcon={<IconUserPlus />}
                onClick={() => setOpenCriarCliente(true)}
              >
                Adicionar cliente
              </Button>

              <CriarClienteDialog
                open={openCriarCliente}
                business={business}
                onClose={() => setOpenCriarCliente(false)}
                limiteAtingido={
                  (clientes?.length || 0) >= (business?.quantidade || 0)
                }
              />
            </Box>
          </PermissaoDisplay>
          <PermissaoDisplay
            match={(claims) => !!claims?.permissoes?.gerenciar_usuarios}
          >
            <Box>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<IconBriefcase />}
                component={Link}
                to="/conta#business"
              >
                Gerenciar escritório
              </Button>
            </Box>
          </PermissaoDisplay>
        </Box>
      ),
    },
    // Some do painel assim que é dispensado, sem deixar o item vazio no grid.
    ...(extratoBannerVisivel()
      ? [{ key: "extrato", xs: 12, render: () => <ExtratoBanner /> }]
      : []),
    {
      key: "clientes",
      xs: 12,
      render: () =>
        (usuario?.layoutPainel || "grade") === "grade" ? (
          <GradeClientes clientes={clientesFiltrados} />
        ) : (
          <ListaClientes clientes={clientesFiltrados} />
        ),
    },
  ];

  return (
    <Container
      maxWidth="lg"
      sx={{
        p: (theme) => ({
          md: theme.spacing(4, 8),
          xs: theme.spacing(2),
        }),
      }}
    >
      <Grid container alignItems="stretch" alignContent="stretch">
        {/* Sem entrada escalonada: o contador volta ao painel dezenas de vezes
            por dia, e o fade em cascata segurava a lista de clientes por ~2 s. */}
        {gridItems.map((item) => (
          <Grid key={item.key} item xs={item.xs || 12} md={item.md} p={1}>
            {item.render()}
          </Grid>
        ))}
      </Grid>
      <Dialog open={limite}>
        <DialogTitle variant="h3">Limite do plano atingido</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Você atingiu o limite de clientes cadastrados para o seu plano
            atual. Para continuar adicionando clientes, por favor, considere
            fazer um upgrade de sua assinatura para um plano que atenda às suas
            necessidades.
            <br />
            Se preferir, você também pode arquivar clientes ativos para liberar
            espaço.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            color="inherit"
            component={Link}
            to="/conta#business"
          >
            Arquivar Clientes
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              abrirAssinatura();
              setLimite(false);
            }}
          >
            Fazer upgrade
          </Button>
        </DialogActions>
      </Dialog>
      <FelixTour />
    </Container>
  );
}
